/* eslint-disable no-console */
import { action, observable, makeObservable, runInAction } from 'mobx'
import SilentListStore from 'stores/SilentListStore'

import CurrentAccountService from 'services/CurrentAccount/CurrentAccountService'

import CurrentAccount from 'models/CurrentAccount'
import Liquidation from 'models/CurrentAccount/Liquidation'
import LiquidationListItem from 'models/CurrentAccount/LiquidationListItem'
import Paginator from 'models/Paginator'

class CurrentAccountStore extends SilentListStore {
    pages = ''
    selectedLiquidation = {}
    currentPage = 1
    paginator = Paginator.empty()
    nameTextSearch = ''
    liquidations = []

    constructor() {
        super({ itemsKey: 'liquidations', cacheExtraKeys: ['pages'] })
        this.currentAccountService = new CurrentAccountService()
        this.currentAccount = new CurrentAccount()
        this.liquidation = new Liquidation()

        this.makeObservables()
    }

    getListCacheKey() {
        return [
            this.nameTextSearch || '',
            this.currentPage,
        ].join('|')
    }

    changePage(page) {
        this.currentPage = page
    }

    filterByText(searchText) {
        this.nameTextSearch = searchText || ''
    }

    async getCurrentAccount({ silent = false } = {}) {
        await this.fetchList({
            silent,
            onRetry: () => this.getCurrentAccount(),
            request: async () => {
                const params = {
                    page: this.currentPage,
                    search: this.nameTextSearch,
                }
                const response = await this.currentAccountService.getCurrentAccount(params)
                const items = (response?.data || []).map((item) => LiquidationListItem.fromJson(item))

                return {
                    items,
                    paginator: response?.paginator || Paginator.empty(),
                    pages: response?.pagination?.pages ?? '',
                }
            },
        })

        runInAction(() => {
            this.currentAccount.setCurrentAccount(this.liquidations)
        })
    }

    async getLiquidation(id) {
        this.preRequest(this.getLiquidation)

        try {
            const { data } = await this.currentAccountService.getLiquidation(id)

            this.liquidation.setLiquidation(data)

            runInAction(() => {
                this.onSuccessRequest()
            })
        } catch (e) {
            this.onErrorRequest(e)
        }
    }

    async uploadBillFile(file) {
        this.preRequest(this.uploadBillFile)

        const { size, name, type } = file?.file

        if (size > 5242880) {
            throw new Error("No se pudo cargar la factura: peso máximo 5 MB")
        }

        try {
            const payload = {
                invoice: {
                    file: [{
                        filename: name,
                        mime: type,
                        content: file?.fileBase64
                    }],
                },
                liquidationId: this.liquidation?.id
            }

            await this.currentAccountService.uploadBillFile(payload)

            this.onSuccessRequest()
            return true
        } catch (error) {
            this.onErrorRequest(error)
            throw new Error(error)
        }
    }

    clearLiquidation() {
        this.liquidation = new Liquidation()
    }

    makeObservables() {
        makeObservable(this, {
            // observables
            currentAccount: observable,
            liquidation: observable,
            liquidations: observable,
            pages: observable,
            selectedLiquidation: observable,
            currentPage: observable,
            nameTextSearch: observable,
            paginator: observable,

            // actions
            changePage: action,
            filterByText: action,
            getCurrentAccount: action,
            getLiquidation: action,
            uploadBillFile: action,
            clearLiquidation: action,
            ...this.getSilentListAnnotations(),
        })
    }
}

export default CurrentAccountStore
